import React from 'react';

import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';

import CanUseFeature from '@dashboard/components/CanUseFeature';
import Link from '@dashboard/components/Link';

import PageEditView from '../../components/PageEditView';

import DynamicPricingForm from './components/DynamicPricingForm';
import GoogleIndexForm from './components/GoogleIndexForm';
import InvoiceSettingsForm from './components/InvoiceSettingsForm';
import NotificationsForm from './components/NotificationsForm';
import RedirectPageForm from './components/RedirectPageForm';
import SlugForm from './components/SlugForm';
import ThirdPartyAnalyticsForm from './components/ThirdPartyAnalyticsForm';
import TicketQRCodesForm from './components/TicketQRCodesForm';

const PageSettings = () => {
  const { page } = usePage();

  return (
    <PageEditView
      tabs={[
        {
          path: '/email-notifications',
          label: 'Email notifications',
          component: <NotificationsForm />,
          width: 'lg',
          ...(page.type === 'checkout'
            ? [
                {
                  description: (
                    <>
                      Customize the email notifications sent to you and your customers after
                      successful checkout.
                    </>
                  ),
                },
              ]
            : []),
          ...(page.type === 'form'
            ? [
                {
                  description: (
                    <>
                      Customize the email notifications sent to you and your customers after
                      submission.
                    </>
                  ),
                },
              ]
            : []),
        },
        ...(page.type === 'checkout' || page.type === 'event'
          ? [
              {
                path: '/invoices',
                label: 'Invoices',
                description: (
                  <>
                    Configure invoice settings for this page. These settings will override your{' '}
                    <Link to="/settings/invoices">default invoice settings</Link>.
                  </>
                ),
                component: <InvoiceSettingsForm />,
              },
            ]
          : []),
        {
          path: '/tracking-pixels',
          label: 'Tracking pixels',
          component: <ThirdPartyAnalyticsForm />,
          width: '2xl',
          description: (
            <>
              Add tracking codes from Google Analytics, Facebook Pixel and others. Simply paste your
              tracking codes into the text box below.{' '}
              <Link to="/help/articles/add-tracking-pixels-and-codes-to-your-checkout" target="_blank">
                Learn more
              </Link>
            </>
          ),
        },
        {
          path: '/dynamic-info',
          label: page.type === 'checkout' ? 'Dynamic price' : 'Dynamic info',
          component: (
            <CanUseFeature feature={Features.DYNAMIC_PRICING}>
              <DynamicPricingForm page={page} />
            </CanUseFeature>
          ),
          width: 'lg',
          description:
            page.type === 'checkout' ? (
              <>
                Dynamically change price and other info on your checkout page via the URL.{' '}
                <Link to="/help/articles/dynamic-price-product-information" color="default" blank>
                  Learn more
                </Link>
              </>
            ) : (
              <>
                Dynamically change info on your checkout page via the URL.{' '}
                <Link to="/help/articles/dynamic-price-product-information" color="default" blank>
                  Learn more
                </Link>
              </>
            ),
        },
        {
          path: '/url-slug',
          label: 'URL Slug',
          component: <SlugForm />,
          width: 'lg',
          description: (
            <>
              Customize the URL slug of your page. Once changed, links shared with the previous URL
              slug will no longer be available.
            </>
          ),
        },
        {
          path: '/google-index',
          label: 'Google index',
          component: <GoogleIndexForm />,
          width: 'lg',
        },
        {
          path: '/redirect-page',
          label: 'Redirect page',
          component: <RedirectPageForm />,
          width: 'lg',
          description: (
            <>
              No longer using this page? Redirect it to another page or URL. Redirects only happen
              when a page is shared as a link; not in pop ups or embeds.
            </>
          ),
        },
        ...(page.type === 'event'
          ? [
              {
                path: '/ticket-qr-codes',
                label: 'Ticket QR codes',
                component: <TicketQRCodesForm />,
              },
            ]
          : []),
      ]}
    />
  );
};

export default PageSettings;
